import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Loader from '../../components/Loader/Loader';
import api from '../../api/index.js';
import s from './Characteristics.module.scss';

const SUPPLIER_LABELS = {
  api_equip: 'Equip',
  api_td: 'TD',
  api_bio: 'Bio',
  api_klen: 'Klen',
  api_practika: 'Practika',
  api_foodatlas: 'Foodatlas',
  api_rusholod: 'Rusholod',
  api_hualian: 'Hualian',
  api_the_matrix: 'The Matrix',
};

const supplierLabel = (api) => SUPPLIER_LABELS[api] || api;

const statusClass = (status) => {
  switch (status) {
    case 'approved':
    case 'ready':
      return s.green;
    case 'rejected':
    case 'blocked':
      return s.red;
    case 'conflict':
      return s.red;
    case 'manual':
      return s.violet;
    case 'needs_attention':
      return s.yellow;
    default:
      return s.yellow;
  }
};

const exportLabel = (status) => {
  switch (status) {
    case 'ready':
      return 'Готово к выгрузке';
    case 'needs_attention':
      return 'Есть расхождения';
    case 'blocked':
      return 'Выгрузка заблокирована';
    case 'draft':
      return 'Не применено';
    default:
      return status;
  }
};

const reviewLabel = (status) => {
  switch (status) {
    case 'approved':
      return 'OK';
    case 'conflict':
      return 'расхождение';
    case 'pending':
      return 'на проверке';
    case 'manual':
      return 'вручную';
    case 'rejected':
      return 'отклонено';
    default:
      return status;
  }
};

const formatRawValue = (value) => {
  const text = String(value ?? '');
  if (text.length > 120) {
    return `${text.slice(0, 120)}…`;
  }
  return text;
};

const candidateText = (item) => {
  const shown = item?.value == null || item.value === '' ? '—' : String(item.value);
  const raw = item?.raw_value == null ? '' : String(item.raw_value);
  if (raw && raw !== shown) {
    return `${shown} (в фиде: ${raw})`;
  }
  return shown;
};

const ProductCharacteristics = () => {
  const { good_id } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('info');

  const load = async () => {
    setLoading(true);
    const data = await api.characteristics.getProduct(good_id);
    if (data.error) {
      setMsg(data.error);
      setMsgType('error');
      setCard(null);
    } else {
      setCard(data);
      setMsg('');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [good_id]);

  const showToast = (text, type = 'info') => {
    setMsg(text);
    setMsgType(type);
  };

  const handleApprove = async (code) => {
    await api.characteristics.approveItem(good_id, code);
    load();
  };

  const handleReject = async (code) => {
    await api.characteristics.rejectItem(good_id, code);
    load();
  };

  const handleApply = async () => {
    setApplying(true);
    showToast('Считаем характеристики…', 'info');
    const res = await api.characteristics.recomputeProduct(good_id, false);
    setApplying(false);
    if (res?.error) {
      showToast(res.error, 'error');
      return;
    }
    showToast('Готово.', 'success');
    load();
  };

  if (loading) return <Loader />;
  if (!card) {
    return (
      <>
        <Header title="Характеристики товара" />
        <p className={msgType === 'error' ? s.msg : s.empty}>{msg || 'Товар не найден'}</p>
      </>
    );
  }

  const meta = card.meta || {};
  const exportStatus = meta.export_status || 'draft';
  const catId = meta.master_category_id || card.category_id;
  const unified = card.unified_characteristics || [];
  const hasUnified = unified.length > 0;
  const rawParams = card.raw_parameters || [];
  const unresolved = hasUnified ? (meta.unresolved || []) : [];
  const masterApi = meta.master_api_name;
  const conflictByCode = Object.fromEntries(
    (meta.conflicts || []).map((item) => [item.code, item]),
  );

  return (
    <>
      <Header
        title="Характеристики товара"
        breadCrumbs={[
          { name: 'Характеристики', link: '/characteristics' },
          ...(catId
            ? [{ name: 'Категория', link: `/characteristics/categories/${catId}` }]
            : []),
          { name: card.title || good_id, link: '' },
        ]}
      />
      <div className={s.wrap}>
        <p className={s.productTitle}>{card.title}</p>

        <div className={s.productHeader}>
          {hasUnified && (
            <span className={statusClass(exportStatus)}>{exportLabel(exportStatus)}</span>
          )}
          <button
            className={hasUnified ? 'btn grey' : 'btn dark'}
            disabled={applying}
            onClick={handleApply}
          >
            {applying ? 'Считаем…' : 'Применить к этому товару'}
          </button>
          {catId && (
            <button
              type="button"
              className="btn dark"
              onClick={() => navigate(`/characteristics/categories/${catId}`)}
            >
              Настроить категорию
            </button>
          )}
        </div>

        {msg && (
          <p className={msgType === 'success' ? s.infoMsg : msgType === 'error' ? s.msg : s.infoMsg}>
            {msg}
          </p>
        )}

        {!hasUnified && (
          <div className={s.stepsBox}>
            <p className={s.stepsTitle}>Характеристики для сайта ещё не посчитаны</p>
            <ol className={s.stepsList}>
              <li>
                Откройте{' '}
                {catId ? (
                  <button
                    type="button"
                    className={s.linkBtn}
                    onClick={() => navigate(`/characteristics/categories/${catId}`)}
                  >
                    настройки категории
                  </button>
                ) : (
                  'настройки категории'
                )}
                {' '}и нажмите «Автонастроить (AI)».
              </li>
              <li>На странице категории нажмите «Применить к товарам» — для всех товаров сразу.</li>
              <li>Или нажмите «Применить к этому товару» выше — только для этой карточки.</li>
            </ol>
            <p className={s.stepsHint}>
              Ниже — сырые поля от поставщика (Equip и др.). Они станут нормальными свойствами
              («Ширина», «Мощность»…) после настройки категории.
            </p>
          </div>
        )}

        {hasUnified && (
          <section>
            <h3 className={s.sectionTitle}>Итоговые характеристики</h3>
            <p className={s.hint}>Так пойдут на сайт после выгрузки.</p>
            <ul className={s.list}>
              {unified.map((item) => {
                const conflict = conflictByCode[item.code];
                const candidates = item.sources?.length
                  ? item.sources
                  : (conflict?.candidates || []);
                const chosenFrom = item.source?.api_name;
                const isConflict = item.review_status === 'conflict' || candidates.length > 1;
                return (
                <li key={item.code} className={s.row}>
                  <div>
                    <strong>{item.title || item.code}</strong>: {item.display || item.value}
                    <span className={statusClass(item.review_status)}> {reviewLabel(item.review_status)}</span>
                  </div>
                  {isConflict && candidates.length > 0 && (
                    <ul className={s.conflictCompare}>
                      {candidates.map((cand, idx) => {
                        const fromMaster = cand.is_master || cand.api_name === masterApi;
                        const chosen = cand.api_name === chosenFrom;
                        return (
                          <li
                            key={`${cand.api_name}-${idx}`}
                            className={chosen ? s.conflictWinner : undefined}
                          >
                            {supplierLabel(cand.api_name)}: {candidateText(cand)}
                            {fromMaster ? ' — мастер' : ''}
                            {chosen ? ' — берём это' : ''}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {item.review_status === 'conflict' && (
                    <div className={s.actions}>
                      <button className="btn dark small" type="button" onClick={() => handleApprove(item.code)}>
                        Оставить «{item.display || item.value}»
                      </button>
                      <button className="btn grey small" type="button" onClick={() => handleReject(item.code)}>
                        Не выводить на сайт
                      </button>
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
            {(meta.missing_required || []).length > 0 && (
              <p className={s.yellow}>Не заполнены обязательные: {meta.missing_required.join(', ')}</p>
            )}
          </section>
        )}

        {unresolved.length > 0 && (
          <section>
            <h3 className={s.sectionTitle}>Поля без настройки в категории</h3>
            <p className={s.hint}>
              Поставщик отдаёт эти поля, но в категории для них ещё нет привязки к нашим свойствам.
              Добавьте mapping в настройках категории.
            </p>
            <ul className={s.list}>
              {unresolved.map((u) => (
                <li key={u.id || `${u.api_name}-${u.name}`} className={s.sub}>
                  {supplierLabel(u.api_name)}: {u.name} = {formatRawValue(u.value)}
                </li>
              ))}
            </ul>
          </section>
        )}

        <details className={s.rawDetails}>
          <summary>
            Данные от поставщика ({rawParams.length} {rawParams.length === 1 ? 'поле' : 'полей'})
          </summary>
          {rawParams.length === 0 ? (
            <p className={s.empty}>Нет данных.</p>
          ) : (
            <ul className={s.rawList}>
              {rawParams.map((r) => (
                <li key={`${r.api_name}-${r.name}`}>
                  <span className={s.rawSupplier}>{supplierLabel(r.api_name)}</span>
                  <span className={s.rawName}>{r.name}</span>
                  <span className={s.rawValue}>{formatRawValue(r.value)}</span>
                </li>
              ))}
            </ul>
          )}
        </details>
      </div>
    </>
  );
};

export default ProductCharacteristics;
